import { Close } from '@material-ui/icons';
import cx from 'classnames';
import { useMemo } from 'react';

import { withStyles, WithStyles } from '@core/theme/utils/with-styles';
import { Dropdown, DropdownComponentProps } from '@shared/components/dropdown-new';
import { Flex } from '@shared/components/flex';
import { AlertTriangleIcon } from '@shared/icons/alert-triangle';
import { CheckCircleIcon } from '@shared/icons/check-circle';
import { InfoIcon } from '@shared/icons/info';
import { ButtonColour } from '@shared/types/common/button';
import { normalizedHTMLText } from '@shared/utils/text';

import { styles } from './Alert.styles';
import { Button, ButtonProps } from '../button-new';

export enum AlertMode {
  Default = 'default',
  Inline = 'inline',
}

export enum AlertType {
  alert = 'alert',
  grey = 'grey',
  info = 'info',
  infoTeal = 'infoTeal',
  lilac = 'lilac',
  success = 'success',
  warning = 'warning',
}

export interface AlertProps extends WithStyles<typeof styles> {
  buttons?: Omit<ButtonProps, 'classes' | 'color' | 'variant'>[];
  customSubMessage?: React.ReactNode;
  dropdown?: Omit<DropdownComponentProps, 'classes'>;
  icon?: string | React.ReactElement;
  message?: string;
  mode?: AlertMode;
  subMessage?: string;
  textAction?: React.ReactElement;
  title?: string;
  type?: AlertType;
  onClose?: () => void;
}

export interface AlertItemProps<TMode extends AlertMode = AlertMode> extends Omit<AlertProps, 'classes'> {
  classes?: Partial<AlertProps['classes']>;
  mode?: TMode;
}

const AlertComponent: React.FC<AlertProps> = ({
  buttons,
  classes,
  customSubMessage,
  dropdown,
  icon,
  message,
  mode = AlertMode.Default,
  subMessage,
  textAction,
  title,
  type = AlertType.grey,
  onClose,
}) => {
  const alertIcon = useMemo(() => {
    if (icon) {
      return <div className={classes.icon}>{icon}</div>;
    }

    switch (type) {
      case AlertType.alert:
        return <InfoIcon classes={{ root: classes.icon }} />;

      case AlertType.grey:
        return <InfoIcon classes={{ root: classes.icon }} />;

      case AlertType.info:
      case AlertType.infoTeal:
      case AlertType.lilac:
        return <InfoIcon classes={{ root: classes.icon }} />;

      case AlertType.success:
        return <CheckCircleIcon classes={{ root: classes.icon }} />;

      case AlertType.warning:
        return <AlertTriangleIcon classes={{ root: classes.icon }} />;
    }
  }, [classes.icon, type]);

  return (
    <div className={cx(classes.root, classes[type], classes[mode])}>
      <div className={classes.content}>
        <Flex
          justifyContent="flex-start"
          alignItems={title ? undefined : 'center'}
          classes={{ root: classes.infoContainer }}
        >
          {alertIcon}
          <Flex direction="column" classes={{ root: classes.textActionsContainer }}>
            {title && <div className={classes.title} dangerouslySetInnerHTML={{ __html: normalizedHTMLText(title) }} />}
            {message && (
              <span className={classes.text} dangerouslySetInnerHTML={{ __html: normalizedHTMLText(message || '') }} />
            )}
            {subMessage && (
              <span className={classes.subText} dangerouslySetInnerHTML={{ __html: normalizedHTMLText(subMessage) }} />
            )}
            {customSubMessage && <span className={classes.subText}>{customSubMessage}</span>}
            {textAction}
            <Flex classes={{ root: cx({ [classes.actionsContainer as string]: buttons || dropdown }) }}>
              {dropdown && <Dropdown color={'secondary'} classes={{ root: classes.dropdownRoot }} {...dropdown} />}
              {buttons &&
                buttons.map((button, index) => (
                  <Button
                    colour={ButtonColour.secondaryColour}
                    key={index}
                    {...button}
                    classes={{ root: cx({ [classes[type] as string]: buttons }, classes.button) }}
                  />
                ))}
            </Flex>
          </Flex>
          {onClose && <Close classes={{ root: classes.closeIcon }} onClick={onClose} />}
        </Flex>
      </div>
    </div>
  );
};

export const Alert = withStyles(styles)(AlertComponent);
